import { memo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { fetchSegments } from "@/store/slices/segments/segments.thunk";
import { fetchAttributeLabels } from "@/store/slices/attributeLabels/attributeLabels.thunk";
import {
  markFiltersReady,
  markSegmentsReady,
} from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { useHasActiveSession } from "@/auth/useHasActiveSession";

/**
 * FetchFiltersAndSegments
 *
 * Renderless orchestrator for the v2 filters/segments catalogs:
 *
 *  1. **Segments catalog fetch** — `fetchSegments()` fires exactly once per
 *     session (ref-guarded against React 18 StrictMode double-mount). The v2
 *     endpoint is market-agnostic; per-market views are derived locally via
 *     selectors, so there is no per-market re-fetch anymore.
 *
 *  2. **Readiness flags** — `markFiltersReady()` / `markSegmentsReady()` fire
 *     whenever the corresponding catalog slice is `initialized` but the
 *     `pendingSelection` ready flag is `false`. This covers both the initial
 *     load AND the atomic `filtersReady:false, segmentsReady:false` reset
 *     that `MarketsDropdownView` dispatches on every market change — since
 *     neither catalog needs re-fetching, the flag is simply flipped back so
 *     `selectionListener`'s prune-cycle guard isn't left blocked.
 *
 *  3. **Attribute labels** — the one genuinely per-market fetch; re-runs
 *     whenever the committed `userSelection.markets` changes.
 */
const FetchFiltersAndSegments: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const markets = useSelector((state: RootState) => state.userSelection.markets);
  const filtersInitialized = useSelector(
    (state: RootState) => state.activeGroup.filters.initialized,
  );
  const segmentsInitialized = useSelector(
    (state: RootState) => state.activeGroup.segments.initialized,
  );
  const filtersReady = useSelector(
    (state: RootState) => state.pendingSelection.filtersReady,
  );
  const segmentsReady = useSelector(
    (state: RootState) => state.pendingSelection.segmentsReady,
  );

  const segmentsFetchedRef = useRef(false);
  const hasSession = useHasActiveSession();

  // Filters catalog arrives via bootstrap. Flip the ready flag whenever it's
  // hydrated but the flag is currently false (initial load, or a market
  // change that reset it).
  useEffect(() => {
    if (filtersInitialized && !filtersReady) dispatch(markFiltersReady());
  }, [filtersInitialized, filtersReady, dispatch]);

  // Fetch the segments catalog exactly once per session.
  // Guard against calling the API when the user is not authenticated — this
  // component lives outside <Auth> and therefore mounts even during the brief
  // window after logout before the page redirect completes. `hasActiveSession`
  // works in both auth modes (the legacy token is always absent under Auth0);
  // it is reactive, so the fetch fires once auth settles rather than never.
  useEffect(() => {
    if (!hasSession) return;
    if (segmentsFetchedRef.current) return;
    segmentsFetchedRef.current = true;
    dispatch(fetchSegments());
  }, [hasSession, dispatch]);

  // Mirror of the filters effect above, for the segments catalog.
  useEffect(() => {
    if (segmentsInitialized && !segmentsReady) dispatch(markSegmentsReady());
  }, [segmentsInitialized, segmentsReady, dispatch]);

  // Attribute labels remain the one per-market-change fetch.
  // Guarded the same way as the segments fetch above — this component lives
  // outside <Auth> and can still be mounted in the brief window after logout
  // before the page redirect completes, which otherwise ships this request
  // with no Authorization header.
  useEffect(() => {
    if (!hasSession) return;
    if (markets.length === 0) return;
    dispatch(fetchAttributeLabels({ marketIds: markets }));
  }, [hasSession, markets, dispatch]);

  return null;
};

export default memo(FetchFiltersAndSegments);
